import type { TFunction } from 'i18next';

import { LineItemKindViewEnum, PricingView } from '@mopla/data-models';
import { formatPriceFromCents } from '@mopla/utils';

import { IBookingDetailsItem } from '../BookingDetailsDropdown';

// Maps each passenger kind to its paired per-passenger deployment kind.
const PASSENGER_TO_DEPLOYMENT = new Map<
	LineItemKindViewEnum,
	LineItemKindViewEnum
>([
	[
		LineItemKindViewEnum.PASSENGER_NO_DISCOUNT,
		LineItemKindViewEnum.DEPLOYMENT_COST_PASSENGER_NO_DISCOUNT,
	],
	[
		LineItemKindViewEnum.PASSENGER_DISCOUNTED_TICKET_PRESENT,
		LineItemKindViewEnum.DEPLOYMENT_COST_PASSENGER_DISCOUNTED_TICKET_PRESENT,
	],
	[
		LineItemKindViewEnum.PASSENGER_NON_DISCOUNTED_TICKET_PRESENT,
		LineItemKindViewEnum.DEPLOYMENT_COST_PASSENGER_NON_DISCOUNTED_TICKET_PRESENT,
	],
	[
		LineItemKindViewEnum.PASSENGER_PERSONAL_DISCOUNT_AVAILABLE,
		LineItemKindViewEnum.DEPLOYMENT_COST_PASSENGER_PERSONAL_DISCOUNT_AVAILABLE,
	],
	[
		LineItemKindViewEnum.PASSENGER_SEVERELY_DISABLED,
		LineItemKindViewEnum.DEPLOYMENT_COST_PASSENGER_SEVERELY_DISABLED,
	],
	[
		LineItemKindViewEnum.PASSENGER_COMPANION_OF_SEVERELY_DISABLED,
		LineItemKindViewEnum.DEPLOYMENT_COST_PASSENGER_COMPANION_OF_SEVERELY_DISABLED,
	],
]);

const DEPLOYMENT_COST_PASSENGER_KINDS = new Set(
	PASSENGER_TO_DEPLOYMENT.values()
);

const DEPLOYMENT_COST_RIDE_KINDS = new Set<LineItemKindViewEnum>([
	LineItemKindViewEnum.DEPLOYMENT_COST_RIDE_NO_DISCOUNT,
	LineItemKindViewEnum.DEPLOYMENT_COST_RIDE_DISCOUNTED_TICKET_PRESENT,
	LineItemKindViewEnum.DEPLOYMENT_COST_RIDE_NON_DISCOUNTED_TICKET_PRESENT,
	LineItemKindViewEnum.DEPLOYMENT_COST_RIDE_PERSONAL_DISCOUNT_AVAILABLE,
	LineItemKindViewEnum.DEPLOYMENT_COST_RIDE_SEVERELY_DISABLED,
	LineItemKindViewEnum.DEPLOYMENT_COST_RIDE_COMPANION_OF_SEVERELY_DISABLED,
]);

function getLabel(
	kind: LineItemKindViewEnum,
	count: number,
	t: TFunction
): string {
	if (DEPLOYMENT_COST_PASSENGER_KINDS.has(kind)) {
		return count > 1
			? t('summary.price.deployment_per_passenger_count', { count })
			: t('summary.price.deployment_per_passenger');
	}
	if (DEPLOYMENT_COST_RIDE_KINDS.has(kind)) {
		return t('summary.price.deployment_price_total');
	}
	return t(`summary.price.line_item.${kind}`, { count });
}

export function buildLineItems(
	pricing: PricingView,
	t: TFunction,
	previousPricing?: PricingView | null
): IBookingDetailsItem[] {
	const lineItems = pricing.lineItems ?? [];

	// Index deployment-per-passenger items by kind so passenger rows can look them up.
	const deploymentByKind = new Map(
		lineItems
			.filter((li) =>
				DEPLOYMENT_COST_PASSENGER_KINDS.has(li.kind as LineItemKindViewEnum)
			)
			.map((li) => [li.kind, li])
	);

	// lineItems has at most one entry per kind, so a previous pricing's array
	// can be treated as a kind-keyed lookup for "what did this item cost before".
	const previousByKind = new Map(
		(previousPricing?.lineItems ?? [])
			.filter((li) => li.kind != null)
			.map((li) => [li.kind, li])
	);

	const getPreviousValue = (
		kind: LineItemKindViewEnum,
		currentCost: number
	): string | undefined => {
		const previousCost = previousByKind.get(kind)?.costWithTaxCent;
		if (previousCost == null || previousCost === currentCost) return undefined;
		return formatPriceFromCents(previousCost);
	};

	const items: IBookingDetailsItem[] = [];

	for (const item of lineItems) {
		const kind = item.kind;
		if (!kind || kind === LineItemKindViewEnum.DISCOUNT_VOUCHER) continue;

		// Deployment-per-passenger items are emitted right after their passenger — skip here.
		if (DEPLOYMENT_COST_PASSENGER_KINDS.has(kind)) continue;

		const cost = item.costWithTaxCent ?? 0;
		items.push({
			label: getLabel(kind, item.count ?? 1, t),
			value: formatPriceFromCents(cost),
			previousValue: getPreviousValue(kind, cost),
		});

		// If this is a passenger kind, immediately emit its paired deployment row.
		const deploymentKind = PASSENGER_TO_DEPLOYMENT.get(kind);
		if (deploymentKind) {
			const deployment = deploymentByKind.get(deploymentKind);
			if (deployment) {
				const count = deployment.count ?? 1;
				const deploymentCost = deployment.costWithTaxCent ?? 0;
				items.push({
					label: getLabel(deploymentKind, count, t),
					value: formatPriceFromCents(deploymentCost),
					previousValue: getPreviousValue(deploymentKind, deploymentCost),
				});
			}
		}
	}

	return items;
}
